/**
 * Language themes — Sprint 23 (Workforce route + theme system).
 *
 * A pure labels layer: one workspace-level setting (`workspace_context`
 * key `language_theme`) re-skins the workforce vocabulary across the chrome with
 * ZERO functional difference. Five bundled themes; default Workforce (strongest
 * brand voice). The whole resolver is {@link useThemedLabels}.
 *
 * Adding a label means adding a `LabelKey` and a string for ALL five themes — the
 * `Record<LanguageTheme, ThemedLabels>` type makes a missing one a compile error,
 * which is the spec's guard against "label missing in an obscure corner".
 *
 * Custom / user-defined vocabularies are deferred indefinitely (Q19).
 */

export const LANGUAGE_THEMES = ["workforce", "crew", "studio", "lab", "plain"] as const;
export type LanguageTheme = (typeof LANGUAGE_THEMES)[number];

export const DEFAULT_LANGUAGE_THEME: LanguageTheme = "workforce";

export function isLanguageTheme(value: unknown): value is LanguageTheme {
  return typeof value === "string" && (LANGUAGE_THEMES as readonly string[]).includes(value);
}

/** Display names for the theme picker cards. */
export const LANGUAGE_THEME_NAMES: Record<LanguageTheme, string> = {
  workforce: "Workforce",
  crew: "Crew",
  studio: "Studio",
  lab: "Lab",
  plain: "Plain",
};

/** A one-line flavour blurb shown under each theme card in the picker. */
export const LANGUAGE_THEME_BLURBS: Record<LanguageTheme, string> = {
  workforce: "Hire and supervise your AI workforce.",
  crew: "Recruit and captain your AI crew.",
  studio: "Bring on and direct your AI studio.",
  lab: "Spin up and run your AI lab.",
  plain: "Plain, neutral language. No metaphor.",
};

/**
 * Sprint 23.7 — the short "feel the voice" sample phrase shown on each compact
 * onboarding theme card (the `picker` variant of `<ThemeCard>`).
 */
export const LANGUAGE_THEME_SAMPLE_PHRASE: Record<LanguageTheme, string> = {
  workforce: "Hire your workforce",
  crew: "Recruit your crew",
  studio: "Bring on a specialist",
  lab: "Spin up an agent",
  plain: "Add an agent",
};

/**
 * Every themable string in the Sprint 23 chrome (~20 keys v1; later sprints add
 * more). Keys are intent-named, not literal, so the same key reads differently
 * per theme.
 */
export type LabelKey =
  | "collective" // group noun, Title Case: "Workforce"
  | "collectiveLower" // group noun, mid-sentence: "workforce"
  | "memberSingular" // one agent as a noun: "hire"
  | "addAction" // the hire verb (button / Cmd-K): "Hire"
  | "addActionDesc" // verb + object (tooltip / Cmd-K subtitle): "Hire a new agent"
  | "roleLabel" // an agent's role field heading: "Role"
  | "leader" // the supervising agent noun: "Manager"
  | "statusWorking" // working state word / fallback verb: "Working"
  | "statusAwaiting" // awaiting-input state label: "Awaiting input"
  | "statusAvailable" // available state label: "Available"
  | "unitSingular" // count unit, singular: "hire" (→ "1 hire")
  | "unitPlural" // count unit, plural: "hires" (→ "6 hires")
  | "workingCount" // subhead count noun: "working"
  | "awaitingCount" // subhead count noun: "awaiting input"
  | "availableCount" // subhead count noun: "available"
  | "productivityTitle" // this-week scorecard heading (used from 23.6): "Performance"
  | "hireDate" // joined-date label: "Hired"
  | "profileTitle" // agent profile page kicker (used from 23.6): "Hire"
  | "emptyTitle" // empty-roster headline: "Your workforce is empty"
  | "emptySubtitle" // empty-roster subheading (Sprint 23.7 empty Workforce state)
  | "emptyCta" // empty-roster button: "Hire your first agent"
  | "tagline" // roster subhead tagline: "The team you supervise"
  // Sprint 23.7 — onboarding flow (/welcome) copy.
  | "onboardingWelcomeTitle" // step 1 heading: "Let's build your workforce"
  | "onboardingIntentLabel" // step 1 sub-paragraph: "Tell us what you want done…"
  | "onboardingIntentPlaceholder" // step 1 textarea placeholder
  | "themePickerTitle" // step 2 heading: "Pick your vocabulary"
  | "hireConfirmTitle" // step 3 heading: "Meet your first hire"
  // Sprint 23.5 — Hire wizard.
  | "wizardTitle" // wizard top-bar title: "Hire a new agent" / "Recruit crew" / …
  | "wizardGreeting" // wizard kick-off chat message, with workspace voice
  | "hireConfirmAction" // primary button verb when name set: "Hire" / "Recruit" / …
  // Sprint 23.6 — agent profile (Workforce position sidebar + action row).
  | "workforcePositionTitle" // sidebar section heading: "Workforce position" / "Crew position" / …
  | "reportsToLabel" // reports-to label: "Reports to" / "Captained by" / "Directed by" / "Led by"
  | "managesLabel" // manages-N label: "Manages" / "Captains" / "Directs" / "Leads"
  | "composeRetroAction" // action row button: "Compose retro" (verb-agnostic but themed for flavour)
  | "assignTaskAction" // action row button: "Assign task" / "Set heading" / …
  | "editPersonaAction" // action row button: "Edit persona" / "Adjust profile" / …
  // Sprint 23.6.1 — sidebar section headers (Profile / Equipped with / Specialties).
  | "sidebarProfileLabel" // sidebar Profile section heading
  | "sidebarEquippedWithLabel" // sidebar Equipped with section heading
  | "sidebarSpecialtiesLabel" // sidebar Specialties section heading
  // Sprint 23.6 v2 — sidebar "Edit in settings →" hover affordance.
  | "editInSettingsAction" // sidebar block hover link → Settings tab anchor
  // Sprint 23.8 — workspace home page (the daily briefing at /home).
  | "workspaceHomeAttentionTitle" // needs-attention card heading
  | "workspaceHomePulseTitle" // workforce pulse section heading
  | "workspaceHomeTasksTitle" // active-tasks section heading
  | "workspaceHomeScorecardTitle" // workspace scorecard heading ("This week")
  | "workspaceMissionPlaceholder"; // empty-mission inline-edit placeholder

export type ThemedLabels = Record<LabelKey, string>;

export const LANGUAGE_THEME_LABELS: Record<LanguageTheme, ThemedLabels> = {
  workforce: {
    collective: "Workforce",
    collectiveLower: "workforce",
    memberSingular: "hire",
    addAction: "Hire",
    addActionDesc: "Hire a new agent",
    roleLabel: "Role",
    leader: "Manager",
    statusWorking: "Working",
    statusAwaiting: "Awaiting input",
    statusAvailable: "Available",
    unitSingular: "hire",
    unitPlural: "hires",
    workingCount: "working",
    awaitingCount: "awaiting input",
    availableCount: "available",
    productivityTitle: "Performance",
    hireDate: "Hired",
    profileTitle: "Hire",
    emptyTitle: "Your workforce is empty",
    emptySubtitle:
      "Hire your first agent and watch them get to work. Kuvio is at its best when you have a team to supervise.",
    emptyCta: "Hire your first agent",
    tagline: "The team you supervise",
    onboardingWelcomeTitle: "Let's build your workforce",
    onboardingIntentLabel: "Tell us what you want done. We'll suggest your first hire.",
    onboardingIntentPlaceholder: "e.g. Research inbound leads from my HubSpot pipeline",
    themePickerTitle: "Pick your vocabulary",
    hireConfirmTitle: "Meet your first hire",
    wizardTitle: "Hire a new agent",
    wizardGreeting: "Welcome! Tell me about who you want to hire. Describe their job, what they care about, anything you have in mind.",
    hireConfirmAction: "Hire",
    workforcePositionTitle: "Workforce position",
    reportsToLabel: "Reports to",
    managesLabel: "Manages",
    composeRetroAction: "Compose retro",
    assignTaskAction: "Assign task",
    editPersonaAction: "Edit persona",
    sidebarProfileLabel: "Profile",
    sidebarEquippedWithLabel: "Equipped with",
    sidebarSpecialtiesLabel: "Specialties",
    editInSettingsAction: "Edit in settings →",
    workspaceHomeAttentionTitle: "Needs your attention",
    workspaceHomePulseTitle: "Workforce pulse",
    workspaceHomeTasksTitle: "Active tasks",
    workspaceHomeScorecardTitle: "This week",
    workspaceMissionPlaceholder: "Add a mission — what is this workforce for?",
  },
  crew: {
    collective: "Crew",
    collectiveLower: "crew",
    memberSingular: "crew member",
    addAction: "Recruit",
    addActionDesc: "Recruit a new agent",
    roleLabel: "Role",
    leader: "Captain",
    statusWorking: "On the job",
    statusAwaiting: "Needs you",
    statusAvailable: "Standing by",
    unitSingular: "crew member",
    unitPlural: "crew",
    workingCount: "on the job",
    awaitingCount: "need you",
    availableCount: "standing by",
    productivityTitle: "Output",
    hireDate: "Joined",
    profileTitle: "Crew member",
    emptyTitle: "Your crew is empty",
    emptySubtitle:
      "Recruit your first agent and watch them get to work. Kuvio is at its best when you have a crew to captain.",
    emptyCta: "Recruit your first agent",
    tagline: "The crew you captain",
    onboardingWelcomeTitle: "Let's build your crew",
    onboardingIntentLabel: "Tell us what you want done. We'll suggest your first crew member.",
    onboardingIntentPlaceholder: "e.g. Research inbound leads from my HubSpot pipeline",
    themePickerTitle: "Pick your vocabulary",
    hireConfirmTitle: "Meet your first crew member",
    wizardTitle: "Recruit a crew member",
    wizardGreeting: "Welcome aboard! Tell me about who you want to recruit. Describe their job, what they care about, anything you have in mind.",
    hireConfirmAction: "Recruit",
    workforcePositionTitle: "Crew position",
    reportsToLabel: "Reports to",
    managesLabel: "Captains",
    composeRetroAction: "Log debrief",
    assignTaskAction: "Set heading",
    editPersonaAction: "Edit profile",
    sidebarProfileLabel: "Crew profile",
    sidebarEquippedWithLabel: "Kitted with",
    sidebarSpecialtiesLabel: "Skills",
    editInSettingsAction: "Edit in settings →",
    workspaceHomeAttentionTitle: "Needs your attention",
    workspaceHomePulseTitle: "Crew pulse",
    workspaceHomeTasksTitle: "Active tasks",
    workspaceHomeScorecardTitle: "This week",
    workspaceMissionPlaceholder: "Add a mission — what is this crew for?",
  },
  studio: {
    collective: "Studio",
    collectiveLower: "studio",
    memberSingular: "specialist",
    addAction: "Bring on",
    addActionDesc: "Bring on a new agent",
    roleLabel: "Craft",
    leader: "Director",
    statusWorking: "Creating",
    statusAwaiting: "Awaiting direction",
    statusAvailable: "Open",
    unitSingular: "specialist",
    unitPlural: "specialists",
    workingCount: "creating",
    awaitingCount: "awaiting direction",
    availableCount: "open",
    productivityTitle: "Work this week",
    hireDate: "Joined",
    profileTitle: "Specialist",
    emptyTitle: "Your studio is empty",
    emptySubtitle:
      "Bring on your first agent and watch them get to work. Kuvio is at its best when you have a studio to direct.",
    emptyCta: "Bring on your first agent",
    tagline: "The studio you direct",
    onboardingWelcomeTitle: "Let's build your studio",
    onboardingIntentLabel: "Tell us what you want done. We'll suggest your first specialist.",
    onboardingIntentPlaceholder: "e.g. Research inbound leads from my HubSpot pipeline",
    themePickerTitle: "Pick your vocabulary",
    hireConfirmTitle: "Meet your first specialist",
    wizardTitle: "Bring on a specialist",
    wizardGreeting: "Welcome to the studio! Tell me about who you want to bring on. Describe their craft, what they create, anything you have in mind.",
    hireConfirmAction: "Bring on",
    workforcePositionTitle: "Studio members",
    reportsToLabel: "Directed by",
    managesLabel: "Directs",
    composeRetroAction: "Write review",
    assignTaskAction: "Assign task",
    editPersonaAction: "Edit profile",
    sidebarProfileLabel: "Profile",
    sidebarEquippedWithLabel: "Working with",
    sidebarSpecialtiesLabel: "Strengths",
    editInSettingsAction: "Edit in settings →",
    workspaceHomeAttentionTitle: "Needs your attention",
    workspaceHomePulseTitle: "Studio pulse",
    workspaceHomeTasksTitle: "Active tasks",
    workspaceHomeScorecardTitle: "This week",
    workspaceMissionPlaceholder: "Add a mission — what is this studio for?",
  },
  lab: {
    collective: "Lab",
    collectiveLower: "lab",
    memberSingular: "researcher",
    addAction: "Spin up",
    addActionDesc: "Spin up a new agent",
    roleLabel: "Focus",
    leader: "Lead",
    statusWorking: "Running",
    statusAwaiting: "Awaiting input",
    statusAvailable: "Idle",
    unitSingular: "agent",
    unitPlural: "agents",
    workingCount: "running",
    awaitingCount: "awaiting input",
    availableCount: "idle",
    productivityTitle: "Throughput",
    hireDate: "Started",
    profileTitle: "Researcher",
    emptyTitle: "Your lab is empty",
    emptySubtitle:
      "Spin up your first agent and watch them get to work. Kuvio is at its best when you have a lab to run.",
    emptyCta: "Spin up your first agent",
    tagline: "The lab you run",
    onboardingWelcomeTitle: "Let's build your lab",
    onboardingIntentLabel: "Tell us what you want done. We'll suggest your first researcher.",
    onboardingIntentPlaceholder: "e.g. Research inbound leads from my HubSpot pipeline",
    themePickerTitle: "Pick your vocabulary",
    hireConfirmTitle: "Meet your first researcher",
    wizardTitle: "Spin up a researcher",
    wizardGreeting: "Welcome to the lab! Tell me about who you want to spin up. Describe their focus, what they research, anything you have in mind.",
    hireConfirmAction: "Spin up",
    workforcePositionTitle: "Lab roster",
    reportsToLabel: "Led by",
    managesLabel: "Leads",
    composeRetroAction: "Log findings",
    assignTaskAction: "Assign task",
    editPersonaAction: "Edit profile",
    sidebarProfileLabel: "Spec",
    sidebarEquippedWithLabel: "Equipped",
    sidebarSpecialtiesLabel: "Capabilities",
    editInSettingsAction: "Edit in settings →",
    workspaceHomeAttentionTitle: "Needs your attention",
    workspaceHomePulseTitle: "Lab pulse",
    workspaceHomeTasksTitle: "Active tasks",
    workspaceHomeScorecardTitle: "This week",
    workspaceMissionPlaceholder: "Add a mission — what is this lab for?",
  },
  plain: {
    collective: "Team",
    collectiveLower: "team",
    memberSingular: "agent",
    addAction: "Add",
    addActionDesc: "Add a new agent",
    roleLabel: "Role",
    leader: "Lead",
    statusWorking: "Working",
    statusAwaiting: "Awaiting input",
    statusAvailable: "Available",
    unitSingular: "agent",
    unitPlural: "agents",
    workingCount: "working",
    awaitingCount: "awaiting input",
    availableCount: "available",
    productivityTitle: "Activity",
    hireDate: "Added",
    profileTitle: "Agent",
    emptyTitle: "No agents yet",
    emptySubtitle:
      "Add your first agent and watch them get to work. Kuvio is at its best when you have a team to manage.",
    emptyCta: "Add your first agent",
    tagline: "The team you manage",
    onboardingWelcomeTitle: "Let's build your team",
    onboardingIntentLabel: "Tell us what you want done. We'll suggest your first agent.",
    onboardingIntentPlaceholder: "e.g. Research inbound leads from my HubSpot pipeline",
    themePickerTitle: "Pick your vocabulary",
    hireConfirmTitle: "Meet your first agent",
    wizardTitle: "Add a new agent",
    wizardGreeting: "Tell me about the agent you want to add. Describe their role, what they do, anything you have in mind.",
    hireConfirmAction: "Add",
    workforcePositionTitle: "Hierarchy",
    reportsToLabel: "Reports to",
    managesLabel: "Manages",
    composeRetroAction: "Write retro",
    assignTaskAction: "Assign task",
    editPersonaAction: "Edit agent",
    sidebarProfileLabel: "Profile",
    sidebarEquippedWithLabel: "Tools",
    sidebarSpecialtiesLabel: "Specialties",
    editInSettingsAction: "Edit in settings →",
    workspaceHomeAttentionTitle: "Needs your attention",
    workspaceHomePulseTitle: "Team pulse",
    workspaceHomeTasksTitle: "Active tasks",
    workspaceHomeScorecardTitle: "This week",
    workspaceMissionPlaceholder: "Add a mission — what is this workspace for?",
  },
};
